import { baseClient } from "./baseRequest";

/** Windows the API accepts — Sentry only supports these on the issues endpoint. */
export const STATS_PERIODS = ["1h", "24h", "14d", "30d", "90d"] as const;
export type StatsPeriod = (typeof STATS_PERIODS)[number];

export enum SentryIssueSort {
  LastSeen = "date",
  FirstSeen = "new",
  Events = "freq",
  Users = "user",
}

/** How the domain was worked out, shown as a hint next to the chip. */
export type ErrorDomainSource =
  | "module"
  | "route"
  | "keyword"
  | "project"
  | "unknown";

export interface SentryProject {
  id: string;
  slug: string;
  name: string;
  platform: string | null;
}

export interface SentryIssue {
  id: string;
  shortId: string;
  title: string;
  culprit: string | null;
  errorType: string | null;
  errorValue: string | null;
  filename: string | null;
  functionName: string | null;
  level: string;
  status: string;
  substatus: string | null;
  isUnhandled: boolean;
  priority: string | null;
  projectSlug: string;
  projectId: string;
  domain: string;
  domainSource: ErrorDomainSource;
  count: number;
  lifetimeCount: number;
  userCount: number;
  firstSeen: string | null;
  lastSeen: string | null;
  assignedTo: string | null;
  permalink: string;
  eventCounts: number[];
}

export interface SentryIssuePage {
  issues: SentryIssue[];
  nextCursor: string | null;
}

export interface SentryStackFrame {
  filename: string | null;
  functionName: string | null;
  lineNumber: number | null;
  isInApp: boolean;
}

export interface SentryIssueDetails {
  issue: SentryIssue;
  latestEvent: {
    eventId: string | null;
    dateCreated: string | null;
    release: string | null;
    environment: string | null;
    message: string | null;
    requestUrl: string | null;
    requestMethod: string | null;
    tags: { key: string; value: string }[];
    frames: SentryStackFrame[];
  } | null;
}

export interface ListSentryIssuesParams {
  /** Project slugs; omit for every project in the organization. */
  projects?: string[];
  /** Sentry search query, defaults to `is:unresolved` server-side. */
  query?: string;
  statsPeriod?: StatsPeriod;
  environment?: string;
  sort?: SentryIssueSort;
  limit?: number;
  cursor?: string;
}

export interface SentryIssuesClient {
  listProjects: () => Promise<SentryProject[]>;
  list: (params: ListSentryIssuesParams) => Promise<SentryIssuePage>;
  get: (issueId: string) => Promise<SentryIssueDetails>;
  resolve: (issueId: string) => Promise<SentryIssue>;
  ignore: (issueId: string) => Promise<SentryIssue>;
  unresolve: (issueId: string) => Promise<SentryIssue>;
}

const sentryIssuesClient: SentryIssuesClient = {
  listProjects: () =>
    baseClient.get<SentryProject[]>({ url: "/admin/sentry/projects" }),

  list: ({ projects, ...rest }) =>
    baseClient.get<SentryIssuePage>({
      url: "/admin/sentry/issues",
      params: {
        ...rest,
        // The API takes a comma-separated list of slugs.
        projects: projects?.length ? projects.join(",") : undefined,
      },
    }),

  get: (issueId) =>
    baseClient.get<SentryIssueDetails>({
      url: `/admin/sentry/issues/${issueId}`,
    }),

  resolve: (issueId) =>
    baseClient.post<SentryIssue>({
      url: `/admin/sentry/issues/${issueId}/resolve`,
    }),

  ignore: (issueId) =>
    baseClient.post<SentryIssue>({
      url: `/admin/sentry/issues/${issueId}/ignore`,
    }),

  unresolve: (issueId) =>
    baseClient.post<SentryIssue>({
      url: `/admin/sentry/issues/${issueId}/unresolve`,
    }),
};

export default sentryIssuesClient;
